/**
 * ExamContext.js
 * Context for managing exam session state globally
 *
 * Usage:
 * const { currentExam, startExam, submitAnswer, finishExam } = useExamContext();
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { submitExamResult } from '../services/firebase/examService';

const ExamContext = createContext(null);

export const useExamContext = () => {
  const context = useContext(ExamContext);
  if (!context) {
    throw new Error('useExamContext must be used within ExamProvider');
  }
  return context;
};

export const ExamProvider = ({ children }) => {
  // Current exam session
  const [examSession, setExamSession] = useState(null);

  // Exam results
  const [examResults, setExamResults] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(false);

  // Start a new exam session
  const startExam = useCallback((exam, questions) => {
    setExamSession({
      exam,
      questions,
      startTime: Date.now(),
      answers: {},
      currentQuestionIndex: 0,
      flaggedQuestions: new Set(),
    });
  }, []);

  // Submit answer for a question
  const submitAnswer = useCallback((questionId, answer) => {
    setExamSession(prev => {
      if (!prev) return null;
      return {
        ...prev,
        answers: {
          ...prev.answers,
          [questionId]: answer,
        },
      };
    });
  }, []);

  // Toggle flagged status
  const toggleFlag = useCallback((questionId) => {
    setExamSession(prev => {
      if (!prev) return null;
      const newFlagged = new Set(prev.flaggedQuestions);
      if (newFlagged.has(questionId)) {
        newFlagged.delete(questionId);
      } else {
        newFlagged.add(questionId);
      }
      return {
        ...prev,
        flaggedQuestions: newFlagged,
      };
    });
  }, []);

  // Navigate to question
  const goToQuestion = useCallback((index) => {
    setExamSession(prev => {
      if (!prev) return null;
      return {
        ...prev,
        currentQuestionIndex: index,
      };
    });
  }, []);

  // Finish exam and submit
  const finishExam = useCallback(async () => {
    if (!examSession) return null;

    setLoading(true);
    try {
      const { exam, questions, answers, startTime } = examSession;

      // Calculate score (basic implementation)
      let score = 0;
      questions.forEach(q => {
        const userAnswer = answers[q.key];
        if (userAnswer && String(userAnswer) === String(q.correctAnswer)) {
          score += q.points || 1;
        }
      });

      // Submit to Firestore
      const resultId = await submitExamResult({
        examId: exam.key,
        studentId: exam.studentId,
        score,
        answers,
        submittedAt: new Date().toISOString(),
      });

      const result = {
        resultId,
        score,
        totalQuestions: questions.length,
        answeredCount: Object.keys(answers).length,
        timeSpent: Date.now() - startTime,
      };

      setExamResults(prev => [...prev, result]);
      setExamSession(null);

      return result;
    } catch (error) {
      console.error('Error finishing exam:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [examSession]);

  // Cancel exam session
  const cancelExam = useCallback(() => {
    setExamSession(null);
  }, []);

  // Set exam results (for admin/teacher view)
  const setResults = useCallback((results) => {
    setExamResults(results);
  }, []);

  const value = {
    // State
    examSession,
    examResults,
    loading,

    // Computed
    isInExam: !!examSession,
    progress: examSession ? {
      answered: Object.keys(examSession.answers).length,
      total: examSession.questions.length,
      flagged: examSession.flaggedQuestions.size,
    } : null,

    // Actions
    startExam,
    submitAnswer,
    toggleFlag,
    goToQuestion,
    finishExam,
    cancelExam,
    setResults,
  };

  return (
    <ExamContext.Provider value={value}>
      {children}
    </ExamContext.Provider>
  );
};

export default ExamContext;
